import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface OrderSku {
  SkuId: string;
  ParentSkuId?: string | null;
  Quantity: number;
  Intervals: number;
}

export interface AddToBundleRequest {
  Action: string;
  Description: string;
  OrderSku: OrderSku[];
  OrderId?: number;
  EngagementId?: string | null;
  OpportunityId?: string | null;
}

interface AddToBundleState {
  isLoading: boolean;
  data: AddToBundleRequest | null;
  error: string | null;
}

const initialState: AddToBundleState = {
  isLoading: false,
  data: null,
  error: "",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addToOrderBundle = createAsyncThunk<any, AddToBundleRequest>(
  "CTAddToOrderBundleSlice/addToOrderBundle",
  async (requestDetails, thunkApi) => {
    try {
      const payload: any = {
        Action: requestDetails.Action,
        Description: requestDetails.Description,
        OrderSku: requestDetails.OrderSku.map((sku) => ({
          ...sku,
          Quantity: Number(sku.Quantity),
          Intervals: Number(sku.Intervals),
        })),
        OrderId: requestDetails.OrderId,
      };
      if (requestDetails.EngagementId) {
        payload.EngagementId = requestDetails.EngagementId;
      }
      if (requestDetails.OpportunityId) {
        payload.OpportunityId = requestDetails.OpportunityId;
      }

      const response = await httpClient.post("/order", payload);
      const successStatusCodes = [200, 201, 202];
      if (
        !successStatusCodes.includes(response.status) ||
        response.data.Status !== "success"
      ) {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to add to order bundle.",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response?.data?.Status &&
        error.response?.data?.Status == "error"
      ) {
        return thunkApi.rejectWithValue({
          message: error.response.data.Message,
        });
      } else {
        console.error("🚀 ~ error:", error);
        return thunkApi.rejectWithValue({
          message: "Failed to add to order bundle.",
        });
      }
    }
  }
);

const CTAddToOrderBundleSlice = createSlice({
  name: "CTAddToOrderBundleSlice",
  initialState,
  reducers: {
    resetAddToOrderState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToOrderBundle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        addToOrderBundle.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        }
      )
      .addCase(
        addToOrderBundle.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        }
      );
  },
});

export const { resetAddToOrderState } = CTAddToOrderBundleSlice.actions;

export default CTAddToOrderBundleSlice.reducer;
